/**
 * Чистые хелперы страницы «Сборка и отгрузка».
 * Без импортов React и MUI — подписи, форматтеры, query, фильтры, период.
 */

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

export const MARKETPLACES = ['wb', 'ozon', 'yandex'];
export const DASHBOARD_TABS = ['now', 'analytics'];
export const MAX_PERIOD_DAYS = 92;
export const REFRESH_MS = 60 * 1000;

export const MP_LABELS = { wb: 'WB', ozon: 'Ozon', yandex: 'Яндекс Маркет' };
export const MP_COLORS = { wb: '#CB11AB', ozon: '#005BFF', yandex: '#FFCC00' };
export const MP_TEXT_COLORS = { wb: '#fff', ozon: '#fff', yandex: '#000' };

export const STAGE_LABELS = {
  queue: 'В очереди',
  assembly: 'В сборке',
  picked: 'Собрано, ждёт паллеты',
  on_pallet: 'На паллете',
  shipped: 'Отгружено',
};

export const PHASE_LABELS = { picking: 'Подбор', labeling: 'Проклейка' };

export const PALLET_STATUS_LABELS = { open: 'Открыта', closed: 'Закрыта', loading: 'В погрузке' };

export const LOADING_STATUS_LABELS = {
  open: 'Открыта',
  confirming: 'Подтверждается',
  partial: 'Частично',
  failed: 'Ошибка',
  completed: 'Завершена',
  cancelled: 'Отменена',
  awaiting_wb: 'Ждёт WB',
};

export const PRINT_DOC_LABELS = {
  wb_order_sticker: 'Стикер заказа WB',
  wb_supply_qr: 'QR поставки WB',
  fulfillment_mass_batch: 'Пакет стикеров WB',
  ozon_fulfillment_mass_batch: 'Пакет этикеток Ozon',
  pallet_label: 'Этикетка паллеты',
  ozon_posting_label: 'Этикетка отправления Ozon',
  ozon_carriage_barcode: 'Штрихкод отгрузки Ozon',
  yandex_order_label: 'Ярлык заказа Яндекс',
  yandex_shipment_act: 'Акт отгрузки Яндекс',
};

export const PLAN_KEYS = ['on_time', 'early', 'late'];
export const PLAN_TONES = { on_time: 'success', early: 'warning', late: 'error' };
export const PLAN_NAMES = { on_time: 'в плановую дату', early: 'раньше плана', late: 'позже плана' };

export const ZONE_KEYS = ['good', 'mid', 'bad'];
export const ZONE_TONES = { good: 'success', mid: 'warning', bad: 'error' };
export const ZONE_NAMES = { good: 'норма', mid: 'средне', bad: 'плохо' };

export const DEFAULT_THRESHOLDS = {
  zone_good_max_hours: 18,
  zone_mid_max_hours: 30,
  stuck_hours: 36,
  near_stuck_hours: 30,
};

function finite(n) {
  const v = Number(n);
  return Number.isFinite(v) ? v : 0;
}

function isNum(n) {
  return n !== null && n !== undefined && n !== '' && Number.isFinite(Number(n));
}

function labelOr(map, key) {
  if (key == null || key === '') return '—';
  return map[key] || String(key);
}

export const mpLabel = (mp) => labelOr(MP_LABELS, mp);
export const stageLabel = (stage) => labelOr(STAGE_LABELS, stage);
export const phaseLabel = (phase) => labelOr(PHASE_LABELS, phase);
export const palletStatusLabel = (status) => labelOr(PALLET_STATUS_LABELS, status);
export const loadingStatusLabel = (status) => labelOr(LOADING_STATUS_LABELS, status);
export const printDocLabel = (type) => labelOr(PRINT_DOC_LABELS, type);

export function fmtInt(n) {
  return Math.round(finite(n)).toLocaleString('ru-RU');
}

/** Часы с одним знаком: 12.34 → «12,3 ч», null → «—». */
export function fmtHours(h, { unit = true } = {}) {
  if (!isNum(h)) return '—';
  const s = Number(h).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 1 });
  return unit ? `${s} ч` : s;
}

/** Доля 0..1 → «19 %», null → «—». */
export function fmtShare(share, digits = 0) {
  if (!isNum(share)) return '—';
  return `${(Number(share) * 100).toLocaleString('ru-RU', { maximumFractionDigits: digits })} %`;
}

/** YYYY-MM-DD → DD.MM. */
export function shortDate(ymd) {
  const s = String(ymd || '');
  return YMD_RE.test(s) ? `${s.slice(8, 10)}.${s.slice(5, 7)}` : s;
}

/** YYYY-MM-DD → DD.MM.YYYY. */
export function longDate(ymd) {
  const s = String(ymd || '');
  return YMD_RE.test(s) ? `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}` : s;
}

export function thresholdsOf(...sources) {
  const out = { ...DEFAULT_THRESHOLDS };
  for (const src of sources) {
    if (!src || typeof src !== 'object') continue;
    for (const key of Object.keys(DEFAULT_THRESHOLDS)) {
      if (isNum(src[key]) && Number(src[key]) > 0) out[key] = Number(src[key]);
    }
  }
  return out;
}

/** Подписи зон по порогам: { good: '≤18 ч', mid: '18–30 ч', bad: '>30 ч' }. */
export function zoneRanges(thresholds) {
  const t = thresholdsOf(thresholds);
  const g = fmtHours(t.zone_good_max_hours, { unit: false });
  const m = fmtHours(t.zone_mid_max_hours, { unit: false });
  return { good: `≤${g} ч`, mid: `${g}–${m} ч`, bad: `>${m} ч` };
}

export function zoneOf(hours, thresholds) {
  if (!isNum(hours)) return null;
  const t = thresholdsOf(thresholds);
  const h = Number(hours);
  if (h <= t.zone_good_max_hours) return 'good';
  if (h <= t.zone_mid_max_hours) return 'mid';
  return 'bad';
}

/** Доли зон из счётчиков; при нуле заказов — null по каждой зоне. */
export function zoneShares(zones) {
  const total = ZONE_KEYS.reduce((sum, key) => sum + finite(zones?.[key]), 0);
  const out = {};
  for (const key of ZONE_KEYS) out[key] = total ? finite(zones?.[key]) / total : null;
  return out;
}

export function zonesTotal(zones) {
  return ZONE_KEYS.reduce((sum, key) => sum + finite(zones?.[key]), 0);
}

/** Доли плановых статусов среди отгрузок с плановой датой. */
export function planShares(plan) {
  const rated = finite(plan?.n) - finite(plan?.no_plan);
  const out = {};
  for (const key of PLAN_KEYS) out[key] = rated ? finite(plan?.[key]) / rated : null;
  return out;
}

export function planRated(plan) {
  return Math.max(0, finite(plan?.n) - finite(plan?.no_plan));
}

export function cancelledTotal(row) {
  return finite(row?.cancelled_before) + finite(row?.cancelled_in_assembly) + finite(row?.cancelled_after);
}

/**
 * Текст ошибки для людей: бэкенд местами кладёт JSON-массив шагов
 * `[{"step":"act","message":"..."}]` — разворачиваем в «act: ...».
 */
export function prettyError(raw) {
  if (raw == null) return '';
  const s = String(raw).trim();
  if (!s.startsWith('[') && !s.startsWith('{')) return s;
  try {
    const parsed = JSON.parse(s);
    const list = Array.isArray(parsed) ? parsed : [parsed];
    const parts = list
      .map((it) => {
        if (it == null) return '';
        if (typeof it !== 'object') return String(it);
        const msg = it.message || it.error || it.msg || '';
        if (!msg) return JSON.stringify(it);
        return it.step ? `${it.step}: ${msg}` : String(msg);
      })
      .filter(Boolean);
    return parts.length ? parts.join('; ') : s;
  } catch {
    return s;
  }
}

/** Пояснение к источникам отгрузки. */
export function shippedSourcesText(sources) {
  if (!sources) return '';
  const parts = [
    `закрытие погрузки ${fmtInt(sources.loading)}`,
    `принят МП (WB — скан поставки) ${fmtInt(sources.mp_accept)}`,
    `закрытие отгрузки в кабинете МП ${fmtInt(sources.mp_shipment_close)}`,
  ];
  if (finite(sources.shipped_no_ts) > 0) parts.push(`без отметки времени ${fmtInt(sources.shipped_no_ts)}`);
  return parts.join(' · ');
}

// ============================================================
// Период
// ============================================================

function pad(n) {
  return String(n).padStart(2, '0');
}

export function toYmd(date) {
  if (typeof date === 'string' && YMD_RE.test(date)) return date;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function parseYmd(ymd) {
  const [y, m, d] = String(ymd).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

export function addDays(ymd, delta) {
  const d = parseYmd(ymd);
  d.setDate(d.getDate() + delta);
  return toYmd(d);
}

/** Число дней включительно. */
export function periodDays(from, to) {
  const ms = parseYmd(to).getTime() - parseYmd(from).getTime();
  return Math.round(ms / 86400000) + 1;
}

export const QUICK_RANGES = [
  { kind: 'today', label: 'Сегодня' },
  { kind: 'yesterday', label: 'Вчера' },
  { kind: '7d', label: '7 дней' },
  { kind: '14d', label: '14 дней' },
  { kind: '30d', label: '30 дней' },
];

const QUICK_SPAN = { '7d': 7, '14d': 14, '30d': 30 };

export function quickRange(kind, today) {
  const to = toYmd(today);
  if (!to) return { from: null, to: null };
  if (kind === 'yesterday') {
    const y = addDays(to, -1);
    return { from: y, to: y };
  }
  if (QUICK_SPAN[kind]) return { from: addDays(to, -(QUICK_SPAN[kind] - 1)), to };
  return { from: to, to };
}

export function activeQuickRange(from, to, today = new Date()) {
  return QUICK_RANGES.find((item) => {
    const range = quickRange(item.kind, today);
    return range.from === from && range.to === to;
  })?.kind || null;
}

// ============================================================
// Query и фильтры
// ============================================================

/** `ozon:123` → { mp: 'ozon', id: '123' } или null. */
export function parseWarehouseKey(key) {
  const s = String(key || '');
  const idx = s.indexOf(':');
  if (idx <= 0) return null;
  const mp = s.slice(0, idx);
  const id = s.slice(idx + 1);
  if (!MARKETPLACES.includes(mp) || !id) return null;
  return { mp, id };
}

// Фильтр по товару — те же параметры, что buildFilterParams у ProductFilters, и в URL, и в API.
export const PRODUCT_PARAMS = ['gruppa', 'gruppa_exclude', 'podgruppa', 'podgruppa_exclude', 'article'];

const splitCsv = (value) => String(value || '').split(',').map((s) => s.trim()).filter(Boolean);

/** Оставляет только известные параметры; флаг «кроме» — только при непустом списке. */
export function normalizeProduct(product = {}) {
  const out = {};
  for (const key of ['gruppa', 'podgruppa']) {
    const values = splitCsv(product?.[key]);
    if (!values.length) continue;
    out[key] = values.join(',');
    if (['1', 'true'].includes(String(product[`${key}_exclude`] || ''))) out[`${key}_exclude`] = '1';
  }
  const article = String(product?.article || '').trim();
  if (article) out.article = article;
  return out;
}

/** Параметры → value для ProductFilters. */
export function productFilterValue(product = {}) {
  const p = normalizeProduct(product);
  const value = {};
  for (const key of ['gruppa', 'podgruppa']) {
    const values = splitCsv(p[key]);
    const exclude = p[`${key}_exclude`] === '1';
    value[key] = exclude ? [] : values;
    value[`${key}Mode`] = exclude ? 'exclude' : 'include';
    value[`${key}Excluded`] = exclude ? values : [];
  }
  value.selectedArticle = p.article ? { article: p.article, nmID: '' } : null;
  return value;
}

export function hasProductFilter(product) {
  return Object.keys(normalizeProduct(product)).length > 0;
}

/** МП дашборда → код mp справочника /filters (cards.mp). */
export function productFiltersMp(mp) {
  return { wb: '1', ozon: '2', yandex: '3' }[mp] || '';
}

export function parseQuery(search) {
  const raw = search == null ? '' : String(search);
  const params = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
  const accountRaw = Number(params.get('account_id'));
  const product = {};
  for (const key of PRODUCT_PARAMS) {
    if (params.has(key)) product[key] = params.get(key);
  }
  return {
    tab: params.get('tab'),
    mp: params.get('mp'),
    account_id: Number.isInteger(accountRaw) && accountRaw > 0 ? accountRaw : null,
    warehouse: params.get('warehouse'),
    from: params.get('from'),
    to: params.get('to'),
    product,
  };
}

/**
 * Нормализует состояние: вкладка, МП, кабинет, склад, период (дефолт — 14 дней,
 * не длиннее MAX_PERIOD_DAYS). Склад задаёт МП; без справочников (`meta`)
 * кабинет и склад проверяются только по формату.
 */
export function resolveQuery(query = {}, { meta = null, today = new Date() } = {}) {
  const tab = DASHBOARD_TABS.includes(query.tab) ? query.tab : 'now';
  let mp = MARKETPLACES.includes(query.mp) ? query.mp : '';
  if (mp && Array.isArray(meta?.marketplaces) && !meta.marketplaces.includes(mp)) mp = '';

  let warehouse = '';
  const wh = parseWarehouseKey(query.warehouse);
  if (wh) {
    const known = Array.isArray(meta?.warehouses) ? meta.warehouses.some((w) => w.key === query.warehouse) : true;
    if (known) {
      warehouse = String(query.warehouse);
      mp = wh.mp;
    }
  }

  let accountId = Number.isInteger(Number(query.account_id)) && Number(query.account_id) > 0
    ? Number(query.account_id)
    : null;
  if (accountId != null && Array.isArray(meta?.accounts)) {
    const acc = meta.accounts.find((a) => Number(a.id) === accountId);
    if (!acc || (mp && acc.marketplace !== mp)) accountId = null;
    else if (!mp) mp = acc.marketplace;
  }
  if (warehouse && accountId != null && Array.isArray(meta?.warehouses)) {
    const w = meta.warehouses.find((it) => it.key === warehouse);
    if (w && w.account_id != null && Number(w.account_id) !== accountId) warehouse = '';
  }

  const fallback = quickRange('14d', today);
  let from = query.from && YMD_RE.test(String(query.from)) ? String(query.from) : fallback.from;
  let to = query.to && YMD_RE.test(String(query.to)) ? String(query.to) : fallback.to;
  if (from > to) [from, to] = [to, from];
  if (periodDays(from, to) > MAX_PERIOD_DAYS) from = addDays(to, -(MAX_PERIOD_DAYS - 1));

  return { tab, mp, account_id: accountId, warehouse, from, to, product: normalizeProduct(query.product) };
}

export function buildQuery({ tab, mp, account_id: accountId, warehouse, from, to, product } = {}) {
  const params = new URLSearchParams();
  if (tab) params.set('tab', tab);
  if (mp) params.set('mp', mp);
  if (accountId) params.set('account_id', String(accountId));
  if (warehouse) params.set('warehouse', warehouse);
  for (const [key, value] of Object.entries(normalizeProduct(product))) params.set(key, value);
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  return params.toString();
}

/** Параметры API по фильтрам (пустые не передаём). */
export function filterParams({ mp, account_id: accountId, warehouse, product } = {}) {
  const params = {};
  if (mp) params.marketplace = mp;
  if (accountId) params.account_id = accountId;
  if (warehouse) params.warehouse = warehouse;
  return { ...params, ...normalizeProduct(product) };
}

export function accountsFor(accounts, mp) {
  return (Array.isArray(accounts) ? accounts : [])
    .filter((a) => !mp || a.marketplace === mp)
    .slice()
    .sort((a, b) => {
      if (Boolean(a.active) !== Boolean(b.active)) return a.active ? -1 : 1;
      const mpDiff = MARKETPLACES.indexOf(a.marketplace) - MARKETPLACES.indexOf(b.marketplace);
      if (mpDiff) return mpDiff;
      return String(a.name || '').localeCompare(String(b.name || ''), 'ru');
    });
}

export function warehousesFor(warehouses, mp, accountId) {
  return (Array.isArray(warehouses) ? warehouses : [])
    .filter((w) => (!mp || w.marketplace === mp) && (!accountId || w.account_id == null || Number(w.account_id) === Number(accountId)))
    .slice()
    .sort((a, b) => finite(b.orders_30d) - finite(a.orders_30d));
}

/**
 * Применяет изменение одного фильтра к состоянию:
 * МП сбрасывает несовместимые кабинет/склад, кабинет задаёт МП, склад задаёт МП.
 */
export function applyFilterChange(state, change, meta) {
  const next = { ...state };
  const accounts = Array.isArray(meta?.accounts) ? meta.accounts : [];
  const warehouses = Array.isArray(meta?.warehouses) ? meta.warehouses : [];
  const accountOf = (id) => accounts.find((a) => Number(a.id) === Number(id)) || null;
  const warehouseOf = (key) => warehouses.find((w) => w.key === key) || null;

  if ('mp' in change) {
    next.mp = change.mp || '';
    const acc = accountOf(next.account_id);
    if (next.mp && acc && acc.marketplace !== next.mp) next.account_id = null;
    const wh = parseWarehouseKey(next.warehouse);
    if (!next.mp || (wh && wh.mp !== next.mp)) next.warehouse = '';
  }
  if ('account_id' in change) {
    next.account_id = change.account_id ? Number(change.account_id) : null;
    const acc = accountOf(next.account_id);
    if (acc) next.mp = acc.marketplace;
    const w = warehouseOf(next.warehouse);
    if (w && ((acc && w.account_id != null && Number(w.account_id) !== Number(acc.id)) || (acc && w.marketplace !== acc.marketplace))) {
      next.warehouse = '';
    }
  }
  if ('warehouse' in change) {
    next.warehouse = change.warehouse || '';
    const wh = parseWarehouseKey(next.warehouse);
    if (wh) {
      next.mp = wh.mp;
      const acc = accountOf(next.account_id);
      if (acc && acc.marketplace !== wh.mp) next.account_id = null;
    }
  }
  if ('product' in change) next.product = normalizeProduct(change.product);
  return next;
}

// ============================================================
// Вкладка «Сейчас»
// ============================================================

/** Счётчики и списки «Горит»; списки на бэке обрезаются — счётчики берём из *_total. */
export function hotView(now) {
  const hot = now?.hot || {};
  const part = (listKey, totalKey) => {
    const items = Array.isArray(hot[listKey]) ? hot[listKey] : [];
    const count = finite(hot[totalKey] ?? items.length);
    return { items, count, truncated: items.length < count };
  };
  const stuck = part('stuck', 'stuck_total');
  const near = part('near', 'near_total');
  const dueToday = part('due_today', 'due_today_total');
  return {
    stuck: stuck.items, stuckCount: stuck.count, stuckTruncated: stuck.truncated,
    near: near.items, nearCount: near.count, nearTruncated: near.truncated,
    dueToday: dueToday.items, dueTodayCount: dueToday.count, dueTodayTruncated: dueToday.truncated,
  };
}

/** Есть ли среди МП оцениваемые по плановой дате (Яндекс). */
export function hasPlanRating(tilesOrMps) {
  return (Array.isArray(tilesOrMps) ? tilesOrMps : []).some((t) => (typeof t === 'string' ? t === 'yandex' : t?.rating === 'plan'));
}

/** Плановая дата прошла, а заказ не отгружен. */
export function isPlanOverdue(row, today) {
  return Boolean(row?.rating === 'plan' && row.planned_shipment_day && today && row.planned_shipment_day < today);
}
